import { Inject, Injectable } from '@odinjs/odin';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import TokenConstants from '../../../constants/TokenConstants';
import { JWTTokenUtils } from '../../../utils/jwt-token-utils';
import UserContext from '../../../utils/user-context';
import PseudonymAppointmentModel from '../pseudonym-appointments-model';
import { YES } from '../pseudonym-types';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import PseudonymIntegratedScheduleService from '../pseudonym-service/pseudonym-integrated-schedule-service';

interface AvailableCalendarDaysProps {
  availableDays: Array<string>;
  endAt?: string;
  existsAnyBlockedItems: boolean;
  nrSeqAgeint: number;
  nrSeqAgeintItem: string;
  startAt?: string;
}

const AVAILABLE_CALENDAR_DAYS_DEFAULT = {
  availableDays: [],
  existsAnyBlockedItems: false,
  nrSeqAgeint: 0,
  nrSeqAgeintItem: ''
} as AvailableCalendarDaysProps

@Injectable
export default class PseudonymStepSixService extends PseudonymAppointmentsBaseController {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model?: any;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  public async getAvailableCalendarDays(lastCalendarDate: string): Promise<AvailableCalendarDaysProps> {
    if (this.getIsRescheduleContext()) {
      return this.availableCalendarDay(lastCalendarDate)
        .catch(() => { return AVAILABLE_CALENDAR_DAYS_DEFAULT });
    }

    return this.pseudonymIntegratedScheduleService.getIntegratedScheduleSequenceAndSaveInternalProcedures()
      .then(() => { return this.availableCalendarDay(lastCalendarDate) })
      .catch(() => { return AVAILABLE_CALENDAR_DAYS_DEFAULT });
  }

  public async availableCalendarDay(lastCalendarDate: string): Promise<AvailableCalendarDaysProps> {
    const location = PseudonymAppointmentModel.stepFive.location;

    return HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
      lastCalendarDate,
      locationId: location?.nrSequencia,
      nrSeqAgeint: PseudonymAppointmentModel.integratedScheduleSequence,
      patientId: String(UserContext.patientId)
    })
      .then(response => { return response.body as AvailableCalendarDaysProps })
      .catch(() => { return AVAILABLE_CALENDAR_DAYS_DEFAULT });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public handleAvailableCalendarDaysResponse(response: AvailableCalendarDaysProps, model?: any): void {
    if (response) {
      const panelNotificationSchedulePeriodApi = model.panelNotificationSchedulePeriodApi;
      const datePickerScheduleRange = model.datePickerScheduleRange;
      const { availableDays, nrSeqAgeintItem } = response;

      if (availableDays && availableDays.length > 0) {
        this.getStepSixModel().availableDays = availableDays as [];
      }

      if (this.getStepSixModel().availableDays && this.getStepSixModel().availableDays.length > 0) {
        this.getStepSixModel().availableDays.forEach((date: string) => datePickerScheduleRange.addRange(date, date));
      }

      if (nrSeqAgeintItem) {
        this.context.set({ integratedScheduleItemSequence: nrSeqAgeintItem });
      }

      const isWaitingListConsultationEnabled = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA) === YES;
      const isWaitingListVisible = isWaitingListConsultationEnabled && !availableDays;
      panelNotificationSchedulePeriodApi.setVisible(isWaitingListVisible);
    } else {
      this.restoreInitialRangeDatePickerFromContext();
    }
  }

  public setInitialRangeDatePickerToContext(initialDate: string, finalDate: string): void {
    this.context.set({
      finalDate: finalDate,
      initialDate: initialDate,
    });
  }

  public restoreInitialRangeDatePickerFromContext(): void {
    const datePickerScheduleRange = this.getStepSixModel().datePickerScheduleRange;
    const initialDate = this.context.get('initialDate') as string;
    const finalDate = this.context.get('finalDate') as string;

    datePickerScheduleRange.clearRanges();
    datePickerScheduleRange.addRange(initialDate, finalDate);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getStepSixModel(): any {
    const { stepAnotherPeriod, stepSix } = PseudonymAppointmentModel;
    this.model = stepAnotherPeriod?.isSimplifiedProcess ? stepAnotherPeriod : stepSix;
    return this.model;
  }

}
